// @flow

import React, {useCallback, useEffect, useState} from 'react';
import {
  Divider,
  FormControlLabel,
  Grid,
  Hidden,
  Typography,
  debounce,
  makeStyles,
} from '@material-ui/core';
import {Search, TrendingUp, FlashOn} from '@material-ui/icons';

import {usePage} from '../../page/utils';
import BackSectionButton from '../../../elements/BackSectionButton/BackSectionButton';

import {colors} from '../../../assets/styles/colors';
import FiltersList from './FiltersList/FiltersList';
import IosSwitch from '../../../elements/IosSwitch/IosSwitch';

import {useBindedActions} from '../../../utils/globalStore';

import LoadingSpinner from '../../../elements/LoadingSpinner/LoadingSpinner';
import {useTypedSelector} from '../../../store';
import {actions as ProductFilterActions} from '../duck';

import type {Theme} from '@material-ui/core';
import {useIsFeatureInTier} from '../../../utils/component';
import PremiumFeatureBadge from '../../../elements/PremiumFeatureBadge/PremiumFeatureBadge';
import CustomAlert from '../../../elements/CustomAlert/CustomAlert';

const VECTORIZE_STATUS_MAP = {
  PENDING: {label: 'Pendiente', severity: 'info'},
  ENQUEUED: {label: 'En cola', severity: 'info'},
  QUEUED: {label: 'En cola', severity: 'info'},
  PROCESSING: {label: 'Procesando', severity: 'warning'},
  IN_PROGRESS: {label: 'En progreso', severity: 'warning'},
  INDEXING: {label: 'Indexando', severity: 'warning'},
  COMPLETED: {label: 'Completado', severity: 'success'},
  FINISHED: {label: 'Finalizado', severity: 'success'},
  SUCCESS: {label: 'Éxito', severity: 'success'},
  DONE: {label: 'Completado', severity: 'success'},
  FAILED: {label: 'Fallido', severity: 'error'},
  ERROR: {label: 'Error', severity: 'error'},
};

const DEFAULT_STATUS_INFO = {label: 'Desconocido', severity: 'info'};

const getVectorizeStatusInfo = (state: ?string) => {
  if (!state || typeof state !== 'string') {
    return DEFAULT_STATUS_INFO;
  }
  const statusInfo = VECTORIZE_STATUS_MAP[state.toUpperCase()];
  return statusInfo || {label: state, severity: 'info'};
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    width: '100%',
    boxShadow: '0px 0.5px 2px 0px #60617029',
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    display: 'flex',
    gap: theme.spacing(1),
    borderRadius: 12,
    flexDirection: 'column',
  },
  divider: {
    backgroundColor: colors.grey300,
  },
  title: {
    fontWeight: 600,
    color: colors.grey900,
    fontSize: 16,
  },
  subtitle: {
    color: '#717190',
    fontSize: 14,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  switchLabel: {
    color: colors.grey700,
    fontSize: 14,
  },
  searchCard: {
    width: '100%',
    boxShadow: '0px 0.5px 2px 0px #60617029',
    padding: theme.spacing(2),
    backgroundColor: '#FFFFFF',
    display: 'flex',
    gap: theme.spacing(1),
    borderRadius: 12,
    flexDirection: 'column',
    marginBlock: theme.spacing(2),
  },
  searchLoadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: colors.grey700,
    fontSize: 14,
  },
  infoBanner: {
    marginTop: theme.spacing(0.5),
  },
  searchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  searchTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
  },
  searchIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: colors.primary + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  searchIcon: {
    color: colors.primary,
    fontSize: 22,
  },
  searchTitleContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  searchTitle: {
    fontWeight: 600,
    color: colors.grey900,
    fontSize: 16,
  },
  searchTagline: {
    color: colors.grey600,
    fontSize: 13,
  },
  searchDescription: {
    color: colors.grey700,
    fontSize: 14,
    marginTop: theme.spacing(1),
    lineHeight: 1.5,
  },
  benefitsRow: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1.5),
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  benefitIcon: {
    color: colors.primary,
    fontSize: 18,
  },
  benefitText: {
    color: colors.grey700,
    fontSize: 13,
    fontWeight: 500,
  },
}));

export default usePage(ProductFilters, 'Product filters page');

function ProductFilters() {
  const classes = useStyles();

  const hasProductFiltersFeature = useIsFeatureInTier('PRODUCT_FILTER');
  const hasAdvancedSearchFeature = useIsFeatureInTier(
    'PRODUCT_ADVANCED_SEARCH'
  );
  const [enabled, setEnabled] = useState<boolean>(false);
  const isFetching = useTypedSelector(state => state.productFilters.isFetching);

  const productFiltersSetting = useTypedSelector(
    state => state.productFilters.productFiltersSetting
  );

  const isFetchingSearch = useTypedSelector(
    state => state.productFilters.isFetchingSearch
  );
  const isSavingSearch = useTypedSelector(
    state => state.productFilters.isSavingSearch
  );
  const searchError = useTypedSelector(
    state => state.productFilters.searchError
  );
  const productSearchSetting = useTypedSelector(
    state => state.productFilters.productSearchSetting
  );
  const isFetchingVectorizeStatus = useTypedSelector(
    state => state.productFilters.isFetchingVectorizeStatus
  );
  const vectorizeStatus = useTypedSelector(
    state => state.productFilters.vectorizeStatus
  );
  const vectorizeStatusError = useTypedSelector(
    state => state.productFilters.vectorizeStatusError
  );
  const [searchEnabled, setSearchEnabled] = useState<boolean>(false);
  const [searchSaved, setSearchSaved] = useState<boolean>(false);
  const [initialStatusChecked, setInitialStatusChecked] = useState<boolean>(
    false
  );

  const {
    fetchProductFiltersSetting,
    saveProductFilterSetting,
    fetchProductSearchSetting,
    saveProductSearchSetting,
    fetchVectorizeStatus,
  } = useBindedActions(ProductFilterActions);

  useEffect(() => {
    fetchProductFiltersSetting();
    fetchProductSearchSetting();
  }, [fetchProductFiltersSetting, fetchProductSearchSetting]);

  useEffect(() => {
    if (productFiltersSetting) {
      setEnabled(productFiltersSetting.enabled);
    }
  }, [productFiltersSetting]);

  useEffect(() => {
    if (productSearchSetting) {
      setSearchEnabled(productSearchSetting.enabled);
    }
  }, [productSearchSetting]);

  useEffect(() => {
    if (productSearchSetting && !initialStatusChecked) {
      setInitialStatusChecked(true);
      if (productSearchSetting.enabled) {
        fetchVectorizeStatus();
      }
    }
  }, [productSearchSetting, initialStatusChecked, fetchVectorizeStatus]);

  useEffect(() => {
    let intervalId;
    if (searchEnabled) {
      intervalId = setInterval(() => {
        fetchVectorizeStatus();
      }, 10000);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [searchEnabled, fetchVectorizeStatus]);

  useEffect(() => {
    if (isSavingSearch === false && searchError === undefined && searchSaved) {
      setSearchSaved(false);
      fetchVectorizeStatus();
    }
  }, [isSavingSearch, searchError, searchSaved, fetchVectorizeStatus]);

  const handleSearchEnable = useCallback(
    enabled => {
      setSearchEnabled(enabled);
      setSearchSaved(true);
      saveProductSearchSetting({
        searchSettingRequest: {enabled},
      });
    },
    [saveProductSearchSetting]
  );

  const handleEnable = debounce(enabled => {
    saveProductFilterSetting({
      productFilterSettingRequest: {
        enabled,
      },
    });
  }, 300);

  const action = (
    <FormControlLabel
      disabled={!hasProductFiltersFeature}
      control={
        <IosSwitch
          disabled={!hasProductFiltersFeature}
          checked={enabled}
          onChange={e => handleEnable(e.target.checked)}
        />
      }
      classes={{label: classes.switchLabel}}
      labelPlacement="start"
      label={enabled ? 'Activado' : 'Desactivado'}
    />
  );

  return (
    <div className={classes.root}>
      {isFetching && <LoadingSpinner />}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Hidden smDown>
            <BackSectionButton
              to="/my-store/pages"
              label="Filtros de producto"
            />
          </Hidden>
        </Grid>

        {hasAdvancedSearchFeature && (
          <div className={classes.searchCard}>
            <div className={classes.searchHeader}>
              <div className={classes.searchTitleRow}>
                <div className={classes.searchIconWrapper}>
                  <Search className={classes.searchIcon} />
                </div>
                <div className={classes.searchTitleContent}>
                  <Typography className={classes.searchTitle}>
                    Búsqueda inteligente{' '}
                    {!hasAdvancedSearchFeature && <PremiumFeatureBadge />}
                  </Typography>
                  <Typography className={classes.searchTagline}>
                    Ayuda a tus clientes a encontrar lo que buscan más rápido
                  </Typography>
                </div>
              </div>
              <FormControlLabel
                disabled={!hasAdvancedSearchFeature}
                control={
                  <IosSwitch
                    disabled={
                      !hasAdvancedSearchFeature ||
                      isFetchingSearch ||
                      isSavingSearch
                    }
                    checked={searchEnabled}
                    onChange={e => handleSearchEnable(e.target.checked)}
                  />
                }
                classes={{label: classes.switchLabel}}
                labelPlacement="start"
                label={searchEnabled ? 'Activado' : 'Desactivado'}
              />
            </div>

            <Typography className={classes.searchDescription}>
              Tus clientes podrán encontrar productos incluso si no escriben el
              nombre exacto. El buscador entiende descripciones, características
              y palabras relacionadas.
            </Typography>

            <div className={classes.benefitsRow}>
              <div className={classes.benefitItem}>
                <TrendingUp className={classes.benefitIcon} />
                <Typography className={classes.benefitText}>
                  Más ventas
                </Typography>
              </div>
              <div className={classes.benefitItem}>
                <FlashOn className={classes.benefitIcon} />
                <Typography className={classes.benefitText}>
                  Búsquedas más precisas
                </Typography>
              </div>
            </div>

            {isFetchingSearch && (
              <div className={classes.searchLoadingRow}>
                <LoadingSpinner size={16} />
                <span>Cargando configuración...</span>
              </div>
            )}

            {isSavingSearch && (
              <div className={classes.searchLoadingRow}>
                <LoadingSpinner size={16} />
                <span>Guardando...</span>
              </div>
            )}

            <CustomAlert
              className={classes.infoBanner}
              severity="error"
              open={Boolean(searchError)}
            >
              {searchError || 'Error al guardar la configuración de búsqueda.'}
            </CustomAlert>

            <CustomAlert
              className={classes.infoBanner}
              severity="info"
              open={
                searchEnabled && searchSaved && !isSavingSearch && !searchError
              }
            >
              Estamos preparando tu tienda para recibir búsquedas más
              inteligentes. Esto puede tardar unos minutos.
            </CustomAlert>

            {isFetchingVectorizeStatus && (
              <div className={classes.searchLoadingRow}>
                <LoadingSpinner size={16} />
                <span>Obteniendo estado del proceso...</span>
              </div>
            )}

            <CustomAlert
              className={classes.infoBanner}
              severity="error"
              open={Boolean(vectorizeStatusError)}
            >
              {vectorizeStatusError ||
                'Error al obtener el estado de indexación.'}
            </CustomAlert>

            <CustomAlert
              className={classes.infoBanner}
              severity={
                vectorizeStatus
                  ? getVectorizeStatusInfo(vectorizeStatus.state).severity
                  : 'info'
              }
              open={Boolean(
                vectorizeStatus &&
                  !isFetchingVectorizeStatus &&
                  !vectorizeStatusError
              )}
            >
              {vectorizeStatus && (
                <span>
                  Progreso:{' '}
                  <strong>
                    {getVectorizeStatusInfo(vectorizeStatus.state).label}
                  </strong>
                  {vectorizeStatus.productIds?.productIds != null && (
                    <span>
                      {' — '}
                      {vectorizeStatus.productIds.productIds.length} productos
                      pendientes
                    </span>
                  )}
                </span>
              )}
            </CustomAlert>
          </div>
        )}

        <div className={classes.card}>
          <div className={classes.actions}>
            <Typography className={classes.title}>
              Configurar filtros{' '}
              {!hasProductFiltersFeature && <PremiumFeatureBadge />}
            </Typography>
            <div>{action}</div>
          </div>
          <Typography className={classes.subtitle}>
            Elige las variantes de producto que desees mostrar como filtros.
          </Typography>
          <Divider className={classes.divider} />

          <FiltersList />
        </div>
      </Grid>
    </div>
  );
}

ProductFilters.propTypes = {};
